import { useCallback, useEffect, useState } from 'react';
import type { AdminListResponse, AdminIntegrationRow } from '../../../domain/superadmin/types';
import {
  EMPTY_INTEGRATION_FILTERS,
  type AdminIntegrationFilters,
} from '../../../domain/superadmin/integrationFilters';
import { useAppContext } from '../../../contexts/AppContext';
import { fetchAdminIntegrations } from '../../../services/modules/superadmin/superadminApi';
import { SuperadminApiError } from '../../../services/modules/superadmin/errors';
import { notify } from '../../../utils/toast';

const PAGE_SIZE = 25;

export type { AdminIntegrationFilters };
export { EMPTY_INTEGRATION_FILTERS };

export function useAdminIntegrations() {
  const [data, setData] = useState<AdminListResponse<AdminIntegrationRow> | null>(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [filters, setFilters] = useState<AdminIntegrationFilters>(EMPTY_INTEGRATION_FILTERS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setCurrentPage } = useAppContext();

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchAdminIntegrations({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        type: filters.type || undefined,
        status: filters.status || undefined,
        expiringSoon: filters.expiringSoon || undefined,
      });
      setData(result);
    } catch (e) {
      if (e instanceof SuperadminApiError && e.isForbidden) {
        setCurrentPage('dashboard');
        notify.warning('Acesso não autorizado à área de superadmin.');
        return;
      }
      setError(e instanceof Error ? e.message : 'Erro ao carregar integrações');
    } finally {
      setIsLoading(false);
    }
  }, [page, q, filters, setCurrentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const patchFilters = useCallback((patch: Partial<AdminIntegrationFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  return {
    data,
    isLoading,
    error,
    refetch: load,
    page,
    setPage,
    q,
    setQ,
    filters,
    patchFilters,
    pageSize: PAGE_SIZE,
  };
}
